import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import {
  DropdownOption,
  DropdownOptionsWithNull,
} from '../../lib/Dropdown/types';
import { RootState } from '../../shared/store';
import {
  ActiveIngredients,
  AttachedFile,
  Branch,
  BriefingState,
  ModificationTypes,
  PhRange,
  Prices,
  ProductApplications,
  Step,
  ValueOrNotApplicable,
} from '../../types';

const initialState: BriefingState = {
  // 0: This object should never have this property because Briefing:isIncomplete() depends on it not existing
  steps: [],
  maxStepIndexAlreadyReached: null,
  productName: null,
  productApplications: [],
  productApplicationsOther: null,
  productMarketingClaims: null,
  productMarketingClaimsOther: null,
  formulationType: null,
  formulationTypeOther: null,
  texture: null,
  storage: null,
  productForm: null,
  productFormOther: null,
  textureOther: null,
  storageOther: null,
  requirementsMust: null,
  requirementsMustOther: null,
  requirementsCan: null,
  requirementsCanOther: null,
  restrictionsMust: null,
  restrictionsMustOther: null,
  restrictionsCan: null,
  restrictionsCanOther: null,
  requirementsApplicationConditionDetails: null,
  requirementsPHRange: {},
  requirementsAggressiveComponents: null,
  requirementsLightStability: null,
  targetConsumerAge: null,
  targetConsumerGender: null,
  prices: {},
  additionalInfo: null,
  additionalInfoFiles: null,
  problem: null,
  packaging: null,
  packagingOther: null,
  processingCapabilitiesPackagingDetails: null,
  azelisRawMaterials: [],
  rawMaterial: null,
  activeFunctions: null,
  functionEmulsifier: null,
  functionAdjuvant: null,
  functionStabilizer: null,
  functionSolvent: null,
  functionLiquidColorant: null,
  ingredientsFiles: null,
  activeIngredients: null,
  bakeStability: null,
  bakingTime: null,
  bakingTemperature: null,
  mixingStepDetails: null,
  productPh: null,
  brix: null,
  fillingTime: null,
  fillingTemperature: null,
  foodSafetyDetails: null,
  otherProcessingCapabilities: null,
  processingHotCold: null,
  homogenisation: null,
  finishedProductSamples: null,
  requiredSampleTime: null,
  finishedProductPotential: null,
  projectDeadline: null,
  colourShade: null,
  colourSamples: null,
  colourRequiredTime: null,
  colourLabSupport: null,
  fragranceConcept: null,
  fragranceDirection: null,
  fragranceMust: null,
  fragranceMustOther: null,
  fragranceCMR: null,
  fragranceMaxAmountAllergens: null,
  fragranceRequestedApplicationDosage: null,
  fragranceSamples: null,
  fragranceRequiredSampleTime: null,
  fragranceLargerSample: null,
  modifyBriefingType: null,
  modifyBriefingIssue: null,
};

export const state = createSlice({
  name: 'briefingState',
  initialState,
  reducers: {
    resetAll: () => initialState,
    setProductName: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string>,
    ) => {
      briefingState.productName = payload;
    },
    setProductApplications: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ProductApplications>,
    ) => {
      briefingState.productApplications = payload;
    },
    setProductApplicationsOther: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.productApplicationsOther = payload;
    },
    setProductMarketingClaims: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOptionsWithNull>,
    ) => {
      briefingState.productMarketingClaims =
        Array.isArray(payload) || payload === null ? payload : [payload];
    },
    setProductMarketingClaimsOther: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.productMarketingClaimsOther = payload;
    },
    setFormulationType: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOptionsWithNull>,
    ) => {
      if (!Array.isArray(payload)) {
        briefingState.formulationType = payload;
      }
    },
    setTexture: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOptionsWithNull>,
    ) => {
      if (!Array.isArray(payload)) {
        briefingState.texture = payload;
      }
    },
    setStorage: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOptionsWithNull>,
    ) => {
      if (!Array.isArray(payload)) {
        briefingState.storage = payload;
      }
    },
    setProductForm: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOptionsWithNull>,
    ) => {
      if (!Array.isArray(payload)) {
        briefingState.productForm = payload;
      }
    },
    setRequirementsMust: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[] | null>,
    ) => {
      briefingState.requirementsMust = payload;
    },
    setRequirementsMustOther: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.requirementsMustOther = payload;
    },
    setRequirementsCan: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[] | null>,
    ) => {
      briefingState.requirementsCan = payload;
    },
    setRequirementsCanOther: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.requirementsCanOther = payload;
    },
    setRestrictionsMust: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[] | null>,
    ) => {
      briefingState.restrictionsMust = payload;
    },
    setRestrictionsMustOther: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.restrictionsMustOther = payload;
    },
    setRestrictionsCan: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[] | null>,
    ) => {
      briefingState.restrictionsCan = payload;
    },
    setRestrictionsCanOther: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.restrictionsCanOther = payload;
    },
    setRequirementsApplicationConditionDetails: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.requirementsApplicationConditionDetails = payload;
    },
    setRequirementsPHRange: (
      briefingState: BriefingState,
      { payload }: PayloadAction<PhRange>,
    ) => {
      briefingState.requirementsPHRange = payload;
    },
    setRequirementsAggressiveComponents: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.requirementsAggressiveComponents = payload;
    },
    setRequirementsLightStability: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.requirementsLightStability = payload;
    },
    setMarketingClaimsOther: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.requirementsMustOther = payload;
    },
    setFormulationTypeOther: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.formulationTypeOther = payload;
    },
    setTextureOther: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.textureOther = payload;
    },
    setStorageOther: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.storageOther = payload;
    },
    setProductFormOther: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.productFormOther = payload;
    },
    setTargetConsumerAge: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string[]>,
    ) => {
      briefingState.targetConsumerAge = payload;
    },
    setTargetConsumerGender: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string>,
    ) => {
      briefingState.targetConsumerGender = payload;
    },
    setPrices: (
      briefingState: BriefingState,
      { payload }: PayloadAction<Prices>,
    ) => {
      briefingState.prices = payload;
    },
    setBranchWithSteps: (
      briefingState: BriefingState,
      { payload }: PayloadAction<{ branch: Branch; steps: Step[] }>,
    ) => {
      briefingState.branch = payload.branch;
      briefingState.steps = payload.steps;
    },
    setAdditionalInfo: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string>,
    ) => {
      briefingState.additionalInfo = payload;
    },
    setActiveFunctions: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string[]>,
    ) => {
      briefingState.activeFunctions = payload;
    },
    setFunctionEmulisifer: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[]>,
    ) => {
      briefingState.functionEmulsifier = payload;
    },
    setFunctionLiquidColorant: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[]>,
    ) => {
      briefingState.functionLiquidColorant = payload;
    },
    setFunctionSolvent: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[]>,
    ) => {
      briefingState.functionSolvent = payload;
    },
    setFunctionStabilizer: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[]>,
    ) => {
      briefingState.functionStabilizer = payload;
    },
    setFunctionAdjuvant: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[]>,
    ) => {
      briefingState.functionAdjuvant = payload;
    },
    setProblem: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string>,
    ) => {
      briefingState.problem = payload;
    },
    setActiveIngredients: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ActiveIngredients>,
    ) => {
      briefingState.activeIngredients = payload;
    },
    setAzelisRawMaterials: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[]>,
    ) => {
      briefingState.azelisRawMaterials = payload;
    },
    setRawMaterial: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string>,
    ) => {
      briefingState.rawMaterial = payload;
    },
    setMaxStepIndexAlreadyReached: (
      briefingState: BriefingState,
      { payload }: PayloadAction<number>,
    ) => {
      briefingState.maxStepIndexAlreadyReached = payload;
    },
    setAdditionalInfoFiles: (
      briefingState: BriefingState,
      { payload }: PayloadAction<AttachedFile[]>,
    ) => {
      briefingState.additionalInfoFiles = payload;
    },
    setIngredientsFiles: (
      briefingState: BriefingState,
      { payload }: PayloadAction<AttachedFile[]>,
    ) => {
      briefingState.ingredientsFiles = payload;
    },
    setPackaging: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption | null>,
    ) => {
      briefingState.packaging = payload;
    },
    setPackagingOther: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.packagingOther = payload;
    },
    setBakeStability: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ValueOrNotApplicable | null>,
    ) => {
      briefingState.bakeStability = payload;
    },
    setBakingTime: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ValueOrNotApplicable | null>,
    ) => {
      briefingState.bakingTime = payload;
    },
    setBakingTemperature: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ValueOrNotApplicable | null>,
    ) => {
      briefingState.bakingTemperature = payload;
    },
    setMixingStepDetails: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ValueOrNotApplicable | null>,
    ) => {
      briefingState.mixingStepDetails = payload;
    },
    setProductPh: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ValueOrNotApplicable | null>,
    ) => {
      briefingState.productPh = payload;
    },
    setBrix: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ValueOrNotApplicable | null>,
    ) => {
      briefingState.brix = payload;
    },
    setFillingTime: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ValueOrNotApplicable | null>,
    ) => {
      briefingState.fillingTime = payload;
    },
    setFillingTemperature: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ValueOrNotApplicable | null>,
    ) => {
      briefingState.fillingTemperature = payload;
    },
    setProcessingCapabilitiesPackagingDetails: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ValueOrNotApplicable | null>,
    ) => {
      briefingState.processingCapabilitiesPackagingDetails = payload;
    },
    setFoodSafetyDetails: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ValueOrNotApplicable | null>,
    ) => {
      briefingState.foodSafetyDetails = payload;
    },
    setProcessingHotCold: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ValueOrNotApplicable | null>,
    ) => {
      briefingState.processingHotCold = payload;
    },
    setHomogenisation: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ValueOrNotApplicable | null>,
    ) => {
      briefingState.homogenisation = payload;
    },
    setOtherProcessingCapabilities: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.otherProcessingCapabilities = payload;
    },
    setFinishedProductSamples: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.finishedProductSamples = payload;
    },
    setRequiredSampleTime: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.requiredSampleTime = payload;
    },
    setFinishedProductPotential: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.finishedProductPotential = payload;
    },
    setProjectDeadline: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.projectDeadline = payload;
    },
    setColourShade: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[] | string | null>,
    ) => {
      briefingState.colourShade = payload;
    },
    setColourSamples: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.colourSamples = payload;
    },
    setColourRequiredTime: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.colourRequiredTime = payload;
    },
    setColourLabSupport: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.colourLabSupport = payload;
    },
    setFragranceConcept: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[] | null>,
    ) => {
      briefingState.fragranceConcept = payload;
    },
    setFragranceDirection: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[] | null>,
    ) => {
      briefingState.fragranceDirection = payload;
    },
    setFragranceMust: (
      briefingState: BriefingState,
      { payload }: PayloadAction<DropdownOption[] | null>,
    ) => {
      briefingState.fragranceMust = payload;
    },
    setFragranceMustOther: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.fragranceMustOther = payload;
    },
    setFragranceCMR: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.fragranceCMR = payload;
    },
    setFragranceMaxAmountAllergens: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.fragranceMaxAmountAllergens = payload;
    },
    setFragranceRequestedApplicationDosage: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.fragranceRequestedApplicationDosage = payload;
    },
    setFragranceSamples: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.fragranceSamples = payload;
    },
    setFragranceRequiredSampleTime: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.fragranceRequiredSampleTime = payload;
    },
    setFragranceLargerSample: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.fragranceLargerSample = payload;
    },
    setModifyBriefingType: (
      briefingState: BriefingState,
      { payload }: PayloadAction<ModificationTypes | null>,
    ) => {
      briefingState.modifyBriefingType = payload;
    },
    setModifyBriefingIssue: (
      briefingState: BriefingState,
      { payload }: PayloadAction<string | null>,
    ) => {
      briefingState.modifyBriefingIssue = payload;
    },
  },
});

export const useBriefingState = (): [BriefingState, typeof state.actions] => {
  return [useSelector(({ briefing }: RootState) => briefing), state.actions];
};

export const actions = state.actions;

export const reducers = state.reducer;
